import { GreaterAreaDropdown, NeighborhoodDropdown, StateField } from '@/components/dropdowns';
import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import type { Attraction } from '@/config/objects/attractions';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EntityFormShell,
  FormFieldGroup,
  Input,
  RichTextEditor,
  SwitchField,
  toast,
  useEntityForm,
} from '@startupquickstart/frontend';
import { useNavigate, useParams } from 'react-router-dom';

interface AttractionFormData extends Record<string, unknown> {
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  description: string;
  longDescription: string;
  featuredImageId: string | undefined;
  phoneNumber: string;
  email: string;
  website: string;
  greaterAreaId: string;
  neighborhoodId: string;
  country: string;
  state: string;
  city: string;
  streetAddress: string;
  streetAddress2: string;
  postalCode: string;
}

const DEFAULTS: AttractionFormData = {
  name: '',
  status: 'published',
  description: '',
  longDescription: '',
  featuredImageId: undefined,
  phoneNumber: '',
  email: '',
  website: '',
  greaterAreaId: '',
  neighborhoodId: '',
  country: 'US',
  state: '',
  city: '',
  streetAddress: '',
  streetAddress2: '',
  postalCode: '',
};

export function AttractionFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const form = useEntityForm<AttractionFormData, Attraction>({
    resource: 'attractions',
    itemId,
    defaults: DEFAULTS,
    queryKey: 'Attraction',
    fromApi: (a) => ({
      name: a.name ?? '',
      status: a.status ?? 'published',
      description: a.description ?? '',
      longDescription: a.longDescription ?? '',
      featuredImageId: a.featuredImageId ?? undefined,
      phoneNumber: a.phoneNumber ?? '',
      email: a.email ?? '',
      website: a.website ?? '',
      greaterAreaId: a.greaterAreaId ?? '',
      neighborhoodId: a.neighborhoodId ?? '',
      country: a.country ?? 'US',
      state: a.state ?? '',
      city: a.city ?? '',
      streetAddress: a.streetAddress ?? '',
      streetAddress2: a.streetAddress2 ?? '',
      postalCode: a.postalCode ?? '',
    }),
    toApi: (f) => ({
      name: f.name.trim(),
      status: f.status,
      description: f.description || null,
      longDescription: f.longDescription || null,
      featuredImageId: f.featuredImageId || null,
      phoneNumber: f.phoneNumber.trim() || null,
      email: f.email.trim() || null,
      website: f.website.trim() || null,
      greaterAreaId: f.greaterAreaId || null,
      neighborhoodId: f.neighborhoodId || null,
      country: f.country.trim(),
      state: f.state.trim(),
      city: f.city.trim(),
      streetAddress: f.streetAddress.trim(),
      streetAddress2: f.streetAddress2.trim() || null,
      postalCode: f.postalCode.trim() || null,
    }),
    validate: (f) => {
      const errs: Partial<Record<keyof AttractionFormData, string>> = {};
      if (!f.name.trim()) errs.name = 'Name is required';
      if (!f.greaterAreaId) errs.greaterAreaId = 'Greater Area is required';
      if (!f.country.trim()) errs.country = 'Country is required';
      if (!f.state.trim()) errs.state = 'State is required';
      if (!f.city.trim()) errs.city = 'City is required';
      if (!f.streetAddress.trim()) errs.streetAddress = 'Street address is required';
      // Mirror the backend's email rule (Zod `.email()`): catch obvious typos
      // — missing @, missing domain TLD, embedded spaces — before the request
      // so the user sees an inline field error instead of a generic
      // "Body validation failed" toast from the API.
      if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
        errs.email = 'Please enter a valid email address';
      }
      if (f.website && !/^(https?:\/\/)?[\w.-]+\.\w{2,}/.test(f.website)) {
        errs.website = 'Please enter a valid URL (e.g., https://example.com)';
      }
      return errs;
    },
    onSuccess: (entity, { isEdit: wasEdit }) => {
      if (wasEdit) {
        toast.success('Attraction saved successfully');
        navigate(-1);
      } else {
        toast.success('Attraction created successfully');
        if (entity.id) navigate(`/attractions/${entity.id}`);
      }
    },
    onNotFound: () => {
      toast.error('Attraction not found');
      navigate('/attractions');
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to save attraction');
    },
  });

  return (
    <EntityFormShell
      title={isEdit ? 'Edit Attraction' : 'Create Attraction'}
      subtitle={
        isEdit
          ? form.formData.name || 'Update attraction details'
          : 'Add a new attraction to the platform'
      }
      backHref={isEdit && itemId ? `/attractions/${itemId}` : '/attractions'}
      form={form}
    >
      <div className="space-y-6">
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">General Information</CardTitle>
            <CardDescription>Core attraction details</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup name="name" label="Name" required error={form.errors.name}>
              <Input
                id="name"
                value={form.formData.name}
                onChange={(e) => form.updateField('name', e.target.value)}
                placeholder="Enter attraction name"
                className={cn(form.errors.name && 'border-destructive')}
              />
            </FormFieldGroup>

            <ImageUploadDropzone
              targetAspectRatio={4 / 3}
              value={form.formData.featuredImageId}
              onChange={(value) => form.updateField('featuredImageId', value)}
            />

            <SwitchField
              label="Published"
              description="Make this attraction visible to users"
              checked={form.formData.status === 'published'}
              onCheckedChange={(checked) =>
                form.updateField('status', checked ? 'published' : 'draft')
              }
            />

            <FormFieldGroup name="description" label="Description">
              <RichTextEditor
                value={form.formData.description}
                onChange={(value) => form.updateField('description', value ?? '')}
              />
            </FormFieldGroup>

            <FormFieldGroup name="longDescription" label="Long Description">
              <RichTextEditor
                value={form.formData.longDescription}
                onChange={(value) => form.updateField('longDescription', value ?? '')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Contact Information</CardTitle>
            <CardDescription>Contact details for the attraction</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="phoneNumber" label="Phone Number">
                <Input
                  id="phoneNumber"
                  value={form.formData.phoneNumber}
                  onChange={(e) => form.updateField('phoneNumber', e.target.value)}
                  placeholder="Phone Number"
                />
              </FormFieldGroup>

              <FormFieldGroup name="email" label="Email" error={form.errors.email}>
                <Input
                  id="email"
                  type="email"
                  value={form.formData.email}
                  onChange={(e) => form.updateField('email', e.target.value)}
                  placeholder="Email Address"
                  className={cn(form.errors.email && 'border-destructive')}
                />
              </FormFieldGroup>
            </div>

            <FormFieldGroup name="website" label="Website" error={form.errors.website}>
              <Input
                id="website"
                value={form.formData.website}
                onChange={(e) => form.updateField('website', e.target.value)}
                placeholder="Website URL (e.g., https://example.com)"
                className={cn(form.errors.website && 'border-destructive')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Location</CardTitle>
            <CardDescription>Geographic relationships for the attraction</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup
                name="greaterAreaId"
                label="Greater Area"
                required
                error={form.errors.greaterAreaId}
              >
                <GreaterAreaDropdown
                  value={form.formData.greaterAreaId}
                  onChange={(value) => {
                    form.updateField('greaterAreaId', value);
                    // Neighborhoods are filtered by greater area — clear selection.
                    form.updateField('neighborhoodId', '');
                  }}
                  placeholder="Select Greater Area"
                />
              </FormFieldGroup>

              <FormFieldGroup name="neighborhoodId" label="Neighborhood">
                <NeighborhoodDropdown
                  value={form.formData.neighborhoodId}
                  onChange={(value) => form.updateField('neighborhoodId', value)}
                  placeholder="Select Neighborhood"
                  greaterAreaId={form.formData.greaterAreaId || undefined}
                />
              </FormFieldGroup>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">Address</CardTitle>
            <CardDescription>Physical address of the attraction</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup
              name="streetAddress"
              label="Street Address"
              required
              error={form.errors.streetAddress}
            >
              <Input
                id="streetAddress"
                value={form.formData.streetAddress}
                onChange={(e) => form.updateField('streetAddress', e.target.value)}
                placeholder="Street Address"
                className={cn(form.errors.streetAddress && 'border-destructive')}
              />
            </FormFieldGroup>

            <FormFieldGroup name="streetAddress2" label="Address Line 2">
              <Input
                id="streetAddress2"
                value={form.formData.streetAddress2}
                onChange={(e) => form.updateField('streetAddress2', e.target.value)}
                placeholder="Apartment, suite, etc."
              />
            </FormFieldGroup>

            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="city" label="City" required error={form.errors.city}>
                <Input
                  id="city"
                  value={form.formData.city}
                  onChange={(e) => form.updateField('city', e.target.value)}
                  placeholder="City"
                  className={cn(form.errors.city && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="state" label="State" required error={form.errors.state}>
                <StateField
                  value={form.formData.state}
                  onChange={(value) => form.updateField('state', value)}
                  country={form.formData.country}
                  placeholder="Select State"
                  className={cn(form.errors.state && 'border-destructive')}
                />
              </FormFieldGroup>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <FormFieldGroup name="postalCode" label="Postal Code">
                <Input
                  id="postalCode"
                  value={form.formData.postalCode}
                  onChange={(e) => form.updateField('postalCode', e.target.value)}
                  placeholder="Postal Code"
                />
              </FormFieldGroup>

              <FormFieldGroup name="country" label="Country" required error={form.errors.country}>
                <Input
                  id="country"
                  value={form.formData.country}
                  onChange={(e) => form.updateField('country', e.target.value)}
                  placeholder="Country"
                  className={cn(form.errors.country && 'border-destructive')}
                />
              </FormFieldGroup>
            </div>
          </CardContent>
        </Card>
      </div>
    </EntityFormShell>
  );
}
